/**
 * Piccole regole dei preventivi, in un posto solo: come si legge un importo
 * scritto da una persona, come nasce la riga COD della trattativa, come si
 * racconta un errore del server a chi sta davanti a un cliente.
 */

import { euro } from "@/lib/format";

/** 12.300 € senza centesimi, 5.580,65 € dove servono: così si scrivono i preventivi (TESTI.md). */
export function euroPreciso(valore) {
  const n = Number(valore);
  return Number.isFinite(n) ? euro(n, { centesimi: Math.round(n * 100) % 100 !== 0 }) : "—";
}

/** Il giorno di scadenza di un link: la data che il server ha scritto (23:59:59 UTC di «valido fino»), non la sua ora locale. */
export const giornoScadenza = (scadenza) => (scadenza ? new Date(`${String(scadenza).slice(0, 10)}T12:00:00`) : null);

/** Giorni interi da `da` ad `a`, scritti AAAA-MM-GG. */
export const giorniTra = (da, a) => Math.round((new Date(`${a}T12:00:00`) - new Date(`${da}T12:00:00`)) / 86400000);

/**
 * Un importo come lo scrive un italiano: «10.100,68», «9500,00», «9.500»,
 * «€ 1 250». Accetta anche il punto decimale («10100.68»). Restituisce un
 * numero ≥ 0, oppure `null` se quello che c'è scritto non è un importo.
 *
 * Esiste perché il dialogo della configurazione suggeriva «es. 9500,00» e poi
 * mandava la stringa così com'era: il server la rifiutava, e la configurazione
 * — cioè il prezzo — non si salvava (difetto della call del 23-09).
 */
export function leggiImporto(testo) {
  if (testo === null || testo === undefined) return null;
  if (typeof testo === "number") return Number.isFinite(testo) && testo >= 0 ? testo : null;
  let s = String(testo).replace(/[\s €]/g, "");
  if (!s) return null;
  if (s.includes(",")) {
    // La virgola è il decimale: i punti sono migliaia.
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+$/.test(s)) {
    // «9.500» o «10.100.000»: punti delle migliaia, niente decimali.
    s = s.replace(/\./g, "");
  }
  if (!/^\d+(\.\d+)?$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * La riga che Aleandro ha chiesto in call: «COD <minimo> · COD <massimo>».
 * Il minimo è il prezzo al pavimento del ruolo di chi guarda, il massimo è il
 * livello più alto. Interi, arrotondati, senza separatori né simbolo: il
 * cliente non la capisce, il venditore sa fin dove può scendere.
 */
export function rigaCodice(vendita) {
  if (!vendita) return null;
  const prezzi = Object.values(vendita.prezzi_per_livello || {})
    .map((p) => Number(p?.prezzo_vendita))
    .filter(Number.isFinite);
  const minimo = Number(vendita.prezzo_al_min_ruolo);
  if (!prezzi.length || !Number.isFinite(minimo)) return null;
  return `COD ${Math.round(minimo)} · COD ${Math.round(Math.max(...prezzi))}`;
}

const NOMI_CAMPI = {
  totale_listino: "Totale listino",
  totale_promotion: "Totale promotion",
  totale_composizione: "Totale composizione",
  livello: "Livello",
  livello_ricarico: "Livello",
  etichetta: "Nome",
  nome: "Nome",
  cliente_id: "Cliente",
  contenuto: "Contenuto",
  importo: "Importo",
  percentuale: "Percentuale",
};

/**
 * L'errore del server in una frase da showroom.
 *
 * Il server risponde in tre forme: una frase, un oggetto con `message`, o —
 * quando il dato non passa la validazione — un ELENCO di oggetti. Passare
 * quell'elenco a `toast.error` faceva cadere tutta la pagina («Objects are not
 * valid as a React child»): in produzione, una schermata bianca.
 */
export function messaggioErrore(e, riserva = "Qualcosa non è andato: riprova.") {
  const risposta = e?.response;
  const d = risposta?.data?.detail;
  if (typeof d === "string" && d.trim()) return d;
  if (Array.isArray(d) && d.length) {
    return d
      .map((x) => {
        // Un elemento scritto da noi (senza il `type` di pydantic) ha già la sua frase.
        if (!x?.type && typeof x?.msg === "string" && x.msg.trim()) return x.msg;
        const campo = Array.isArray(x?.loc) ? x.loc[x.loc.length - 1] : null;
        const nome = NOMI_CAMPI[campo] || campo || "Un campo";
        if (x?.type === "decimal_parsing" || x?.type === "float_parsing") {
          return `${nome}: scrivi solo il numero, per esempio 9.500,00`;
        }
        if (x?.type === "missing") return `${nome}: manca`;
        return `${nome}: valore non valido`;
      })
      .join(" · ");
  }
  if (d && typeof d === "object") {
    if (typeof d.message === "string") return d.message;
    if (typeof d.messaggio === "string") return d.messaggio;
    if (typeof d.errore === "string") return d.errore;
  }
  if (risposta && risposta.status >= 500) {
    return "Il gestionale non ha risposto come doveva. Riprova tra un momento.";
  }
  if (!risposta && e?.message === "Network Error") {
    return "Nessuna risposta dal gestionale: controlla la connessione e riprova.";
  }
  return riserva;
}
